import type { TypedSupabaseClient } from '../client';
import type {
  IOnlineCustomerRepository,
  IOrderAttributionRepository,
  IShipmentRepository,
  IStorefrontCheckoutRepository,
  IStorefrontCustomerAccountRepository,
  IStorefrontAnalyticsRepository,
  OnlineCustomer,
  CreateOnlineCustomerInput,
  UpdateOnlineCustomerInput,
  OnlineCustomerFilters,
  OrderAttribution,
  CreateOrderAttributionInput,
  Shipment,
  CreateShipmentInput,
  UpdateShipmentInput,
  ShipmentStatus,
  CheckoutSubmitInput,
  CheckoutSubmitResult,
  SaveStorefrontAccountProfileInput,
  StorefrontAccountOrder,
  StorefrontAccountOrderLine,
  StorefrontAnalytics,
  StorefrontAnalyticsRange,
  TopProductRollup,
  AttributionRollup,
  CityRollup,
} from '../../../interfaces/src';

// Cast helper that preserves `this` binding — calling `client.from(...)` keeps
// the internal context Supabase needs. Returning just `client.from` would
// detach `this` and hang the request.
const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);

// ============================================================
// ONLINE CUSTOMERS
// ============================================================

export class SupabaseOnlineCustomerRepository implements IOnlineCustomerRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: OnlineCustomerFilters): Promise<OnlineCustomer[]> {
    let q = anyFrom(this.supabase)('online_customers').select('*');
    if (filters?.isBlocked !== undefined) q = q.eq('is_blocked', filters.isBlocked);
    if (filters?.city) q = q.eq('city', filters.city);
    if (filters?.isLinked === true) q = q.not('linked_customer_id', 'is', null);
    if (filters?.isLinked === false) q = q.is('linked_customer_id', null);
    if (filters?.isRepeat === true) q = q.gte('total_orders', 2);
    if (filters?.isRepeat === false) q = q.lt('total_orders', 2);
    if (filters?.search) {
      const s = filters.search;
      q = q.or(`phone.ilike.%${s}%,email.ilike.%${s}%,full_name.ilike.%${s}%,first_name.ilike.%${s}%,last_name.ilike.%${s}%`);
    }
    const { data, error } = await q.order('last_order_at', { ascending: false, nullsFirst: false });
    if (error) throw error;
    return (data ?? []).map(mapOnlineCustomer);
  }

  async getById(id: string): Promise<OnlineCustomer | null> {
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapOnlineCustomer(data) : null;
  }

  async getByPhone(phone: string): Promise<OnlineCustomer | null> {
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .select('*').eq('phone', normalizePhone(phone)).maybeSingle();
    if (error) throw error;
    return data ? mapOnlineCustomer(data) : null;
  }

  async create(input: CreateOnlineCustomerInput): Promise<OnlineCustomer> {
    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .insert({
        phone: normalizePhone(input.phone),
        email: input.email ?? null,
        first_name: input.firstName ?? null,
        last_name: input.lastName ?? null,
        full_name: input.fullName ?? joinName(input.firstName, input.lastName),
        city: input.city ?? null,
        primary_city: input.city ?? null,
        address: input.address ?? null,
        preferred_language: input.preferredLanguage ?? 'fr',
        notes: input.notes ?? null,
        first_utm_source: input.firstUtmSource ?? null,
        first_utm_campaign: input.firstUtmCampaign ?? null,
      })
      .select().single();
    if (error) throw error;
    return mapOnlineCustomer(data);
  }

  async update(id: string, input: UpdateOnlineCustomerInput): Promise<OnlineCustomer> {
    const map: Record<string, string> = {
      phone: 'phone',
      email: 'email',
      firstName: 'first_name',
      lastName: 'last_name',
      fullName: 'full_name',
      city: 'city',
      address: 'address',
      preferredLanguage: 'preferred_language',
      isBlocked: 'is_blocked',
      blockReason: 'block_reason',
      linkedCustomerId: 'linked_customer_id',
      notes: 'notes',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col) patch[col] = k === 'phone' && typeof v === 'string' ? normalizePhone(v) : v;
    }
    if (input.city !== undefined) patch['primary_city'] = input.city;
    if (input.isBlocked === true) patch['blocked_at'] = new Date().toISOString();
    if (input.isBlocked === false) patch['blocked_at'] = null;

    const { data, error } = await anyFrom(this.supabase)('online_customers')
      .update(patch).eq('id', id).select().single();
    if (error) throw error;
    return mapOnlineCustomer(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('online_customers').delete().eq('id', id);
    if (error) throw error;
  }

  async block(id: string, reason: string): Promise<OnlineCustomer> {
    return this.update(id, { isBlocked: true, blockReason: reason });
  }

  async unblock(id: string): Promise<OnlineCustomer> {
    return this.update(id, { isBlocked: false, blockReason: null });
  }

  async link(id: string, customerId: string): Promise<OnlineCustomer> {
    return this.update(id, { linkedCustomerId: customerId });
  }

  async unlink(id: string): Promise<OnlineCustomer> {
    return this.update(id, { linkedCustomerId: null });
  }

  async recomputeAggregates(id: string): Promise<OnlineCustomer> {
    const { data: orders, error } = await anyFrom(this.supabase)('sales_documents')
      .select('total_ttc, doc_date, created_at')
      .eq('online_customer_id', id)
      .eq('doc_type', 'order');
    if (error) throw error;
    const list = (orders ?? []) as Array<{ total_ttc: number; doc_date: string; created_at: string }>;
    const totalOrders = list.length;
    const totalSpent = list.reduce((sum, r) => sum + Number(r.total_ttc || 0), 0);
    const firstOrderAt = list.length ? list.reduce((min, r) => (r.created_at < min ? r.created_at : min), list[0]!.created_at) : null;
    const lastOrderAt = list.length ? list.reduce((max, r) => (r.created_at > max ? r.created_at : max), list[0]!.created_at) : null;

    const { data, error: upErr } = await anyFrom(this.supabase)('online_customers')
      .update({
        total_orders: totalOrders,
        total_spent: totalSpent,
        first_order_at: firstOrderAt,
        last_order_at: lastOrderAt,
      })
      .eq('id', id)
      .select()
      .single();
    if (upErr) throw upErr;
    return mapOnlineCustomer(data);
  }
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

function joinName(first?: string | null, last?: string | null): string | null {
  const parts = [first, last].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

function mapOnlineCustomer(row: Record<string, unknown>): OnlineCustomer {
  return {
    id: row['id'] as string,
    phone: row['phone'] as string,
    email: (row['email'] as string) ?? null,
    firstName: (row['first_name'] as string) ?? null,
    lastName: (row['last_name'] as string) ?? null,
    fullName: (row['full_name'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    primaryCity: (row['primary_city'] as string) ?? null,
    preferredLanguage: (row['preferred_language'] as string) ?? 'fr',
    totalOrders: (row['total_orders'] as number) ?? 0,
    totalSpent: Number(row['total_spent'] ?? 0),
    firstOrderAt: (row['first_order_at'] as string) ?? null,
    lastOrderAt: (row['last_order_at'] as string) ?? null,
    isBlocked: (row['is_blocked'] as boolean) ?? false,
    blockReason: (row['block_reason'] as string) ?? null,
    blockedAt: (row['blocked_at'] as string) ?? null,
    linkedCustomerId: (row['linked_customer_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    firstUtmSource: (row['first_utm_source'] as string) ?? null,
    firstUtmCampaign: (row['first_utm_campaign'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

// ============================================================
// ORDER ATTRIBUTION
// ============================================================

export class SupabaseOrderAttributionRepository implements IOrderAttributionRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getBySalesDocumentId(salesDocumentId: string): Promise<OrderAttribution | null> {
    const { data, error } = await anyFrom(this.supabase)('order_attribution')
      .select('*').eq('sales_document_id', salesDocumentId).maybeSingle();
    if (error) throw error;
    return data ? mapAttribution(data) : null;
  }

  async create(input: CreateOrderAttributionInput): Promise<OrderAttribution> {
    const { data, error } = await anyFrom(this.supabase)('order_attribution')
      .insert({
        sales_document_id: input.salesDocumentId,
        utm_source: input.utmSource ?? null,
        utm_medium: input.utmMedium ?? null,
        utm_campaign: input.utmCampaign ?? null,
        utm_content: input.utmContent ?? null,
        utm_term: input.utmTerm ?? null,
        landing_page: input.landingPage ?? null,
        referrer: input.referrer ?? null,
        ad_platform: input.adPlatform ?? null,
        ad_id: input.adId ?? null,
        adset_id: input.adsetId ?? null,
        campaign_id: input.campaignId ?? null,
        ad_name: input.adName ?? null,
        adset_name: input.adsetName ?? null,
        campaign_name: input.campaignName ?? null,
        user_agent: input.userAgent ?? null,
        ip_address: input.ipAddress ?? null,
      })
      .select().single();
    if (error) throw error;
    return mapAttribution(data);
  }
}

function mapAttribution(row: Record<string, unknown>): OrderAttribution {
  return {
    id: row['id'] as string,
    salesDocumentId: row['sales_document_id'] as string,
    utmSource: (row['utm_source'] as string) ?? null,
    utmMedium: (row['utm_medium'] as string) ?? null,
    utmCampaign: (row['utm_campaign'] as string) ?? null,
    utmContent: (row['utm_content'] as string) ?? null,
    utmTerm: (row['utm_term'] as string) ?? null,
    landingPage: (row['landing_page'] as string) ?? null,
    referrer: (row['referrer'] as string) ?? null,
    adPlatform: (row['ad_platform'] as string) ?? null,
    adId: (row['ad_id'] as string) ?? null,
    adsetId: (row['adset_id'] as string) ?? null,
    campaignId: (row['campaign_id'] as string) ?? null,
    adName: (row['ad_name'] as string) ?? null,
    adsetName: (row['adset_name'] as string) ?? null,
    campaignName: (row['campaign_name'] as string) ?? null,
    userAgent: (row['user_agent'] as string) ?? null,
    ipAddress: (row['ip_address'] as string) ?? null,
    capturedAt: row['captured_at'] as string,
    createdAt: row['created_at'] as string,
  };
}

// ============================================================
// SHIPMENTS
// ============================================================

export class SupabaseShipmentRepository implements IShipmentRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listBySalesDocumentId(salesDocumentId: string): Promise<Shipment[]> {
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .select('*').eq('sales_document_id', salesDocumentId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapShipment);
  }

  async getById(id: string): Promise<Shipment | null> {
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapShipment(data) : null;
  }

  async create(input: CreateShipmentInput): Promise<Shipment> {
    const { data, error } = await anyFrom(this.supabase)('shipments')
      .insert({
        sales_document_id: input.salesDocumentId,
        courier_name: input.courierName ?? null,
        tracking_number: input.trackingNumber ?? null,
        shipment_status: input.shipmentStatus ?? 'pending',
        expected_delivery_date: input.expectedDeliveryDate ?? null,
        delivery_fee: input.deliveryFee ?? 0,
        return_fee: input.returnFee ?? 0,
        delivery_address: input.deliveryAddress ?? null,
        delivery_city: input.deliveryCity ?? null,
        delivery_phone: input.deliveryPhone ?? null,
        notes: input.notes ?? null,
        provider_id: input.providerId ?? null,
        provider_external_id: input.providerExternalId ?? null,
        provider_status_code: input.providerStatusCode ?? null,
        cod_amount: input.codAmount ?? 0,
        package_size: input.packageSize ?? 1,
      })
      .select().single();
    if (error) throw error;
    return mapShipment(data);
  }

  async update(id: string, input: UpdateShipmentInput): Promise<Shipment> {
    const map: Record<string, string> = {
      courierName: 'courier_name',
      trackingNumber: 'tracking_number',
      shipmentStatus: 'shipment_status',
      expectedDeliveryDate: 'expected_delivery_date',
      deliveryFee: 'delivery_fee',
      returnFee: 'return_fee',
      deliveryAddress: 'delivery_address',
      deliveryCity: 'delivery_city',
      deliveryPhone: 'delivery_phone',
      notes: 'notes',
      providerId: 'provider_id',
      providerExternalId: 'provider_external_id',
      providerStatusCode: 'provider_status_code',
      codAmount: 'cod_amount',
      packageSize: 'package_size',
      lastSyncedAt: 'last_synced_at',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col) patch[col] = v;
    }
    if (input.shipmentStatus === 'dispatched') patch['dispatched_at'] = new Date().toISOString();
    if (input.shipmentStatus === 'delivered') patch['delivered_at'] = new Date().toISOString();
    if (input.shipmentStatus === 'returned') patch['returned_at'] = new Date().toISOString();

    const { data, error } = await anyFrom(this.supabase)('shipments')
      .update(patch).eq('id', id).select().single();
    if (error) throw error;
    return mapShipment(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('shipments').delete().eq('id', id);
    if (error) throw error;
  }
}

function mapShipment(row: Record<string, unknown>): Shipment {
  return {
    id: row['id'] as string,
    salesDocumentId: row['sales_document_id'] as string,
    courierName: (row['courier_name'] as string) ?? null,
    trackingNumber: (row['tracking_number'] as string) ?? null,
    shipmentStatus: (row['shipment_status'] as ShipmentStatus) ?? 'pending',
    dispatchedAt: (row['dispatched_at'] as string) ?? null,
    deliveredAt: (row['delivered_at'] as string) ?? null,
    returnedAt: (row['returned_at'] as string) ?? null,
    expectedDeliveryDate: (row['expected_delivery_date'] as string) ?? null,
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    returnFee: Number(row['return_fee'] ?? 0),
    deliveryAddress: (row['delivery_address'] as string) ?? null,
    deliveryCity: (row['delivery_city'] as string) ?? null,
    deliveryPhone: (row['delivery_phone'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    providerId: (row['provider_id'] as string | null) ?? null,
    providerExternalId: (row['provider_external_id'] as string | null) ?? null,
    providerStatusCode: row['provider_status_code'] == null ? null : Number(row['provider_status_code']),
    codAmount: Number(row['cod_amount'] ?? 0),
    packageSize: Number(row['package_size'] ?? 1),
    lastSyncedAt: (row['last_synced_at'] as string | null) ?? null,
  };
}

// ============================================================
// STOREFRONT CHECKOUT (public submission)
// ============================================================

export class SupabaseStorefrontCheckoutRepository implements IStorefrontCheckoutRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async submit(input: CheckoutSubmitInput): Promise<CheckoutSubmitResult> {
    const rpcResult = await this.submitViaRpc(input);
    if (rpcResult) return rpcResult;

    const { customer, items, attribution, notes } = input;
    if (!items.length) throw new Error('empty_cart');

    // 1. Resolve storefront products + articles (price, VAT, name)
    const productIds = items.map((it) => it.storefrontProductId);
    const { data: products, error: productsErr } = await anyFrom(this.supabase)('storefront_products')
      .select('id, tenant_id, article_id, price_override, name_fr, name_ar, name_en, articles(name, sale_price_ttc, vat_rate)')
      .in('id', productIds)
      .eq('is_published', true);
    if (productsErr) throw productsErr;
    const productList = (products ?? []) as any[];

    if (productList.length !== productIds.length) {
      throw new Error('product_not_available');
    }
    const tenantId = productList[0]?.tenant_id as string | undefined;
    if (!tenantId) throw new Error('tenant_not_found');
    if (productList.some((p) => p.tenant_id !== tenantId)) {
      throw new Error('product_not_available');
    }

    // 2. Compute line totals and grand totals
    type Line = {
      storefrontProductId: string;
      articleId: string;
      quantity: number;
      unitPriceTtc: number;
      unitPriceHt: number;
      vatRate: number;
      description: string;
      totalHt: number;
      totalTtc: number;
    };
    const lines: Line[] = items.map((item) => {
      const p = productList.find((x: any) => x.id === item.storefrontProductId)!;
      const article = p.articles ?? {};
      const priceTtc = p.price_override != null ? Number(p.price_override) : Number(article.sale_price_ttc || 0);
      const vatRate = Number(article.vat_rate || 0);
      const priceHt = vatRate > 0 ? priceTtc / (1 + vatRate / 100) : priceTtc;
      const totalTtc = round3(priceTtc * item.quantity);
      const totalHt = round3(priceHt * item.quantity);
      const name = p.name_fr ?? p.name_en ?? article.name ?? '';
      return {
        storefrontProductId: p.id,
        articleId: p.article_id,
        quantity: item.quantity,
        unitPriceTtc: priceTtc,
        unitPriceHt: priceHt,
        vatRate,
        description: name,
        totalHt,
        totalTtc,
      };
    });

    const totalHt = round3(lines.reduce((s, l) => s + l.totalHt, 0));
    const itemsTtc = round3(lines.reduce((s, l) => s + l.totalTtc, 0));
    const totalVat = round3(itemsTtc - totalHt);

    // Pull the per-store delivery fee. It's added to every storefront order
    // and frozen on the row, so changing the setting later doesn't alter
    // historical orders.
    const deliveryFee = await this.fetchStoreDeliveryFee(tenantId);
    const totalTtc = round3(itemsTtc + deliveryFee);

    // 3. Upsert online customer by phone
    const phone = normalizePhone(customer.phone);
    let existing: OnlineCustomer | null = null;
    {
      const { data, error } = await anyFrom(this.supabase)('online_customers')
        .select('*').eq('tenant_id', tenantId).eq('phone', phone).maybeSingle();
      if (error) throw error;
      if (data) existing = mapOnlineCustomer(data);
    }

    if (existing?.isBlocked) throw new Error('customer_blocked');

    let onlineCustomerId: string;
    if (existing) {
      const patch: Record<string, unknown> = {
        total_orders: existing.totalOrders + 1,
        total_spent: Number(existing.totalSpent) + totalTtc,
        last_order_at: new Date().toISOString(),
      };
      if (!existing.firstOrderAt) patch['first_order_at'] = new Date().toISOString();
      if (customer.fullName) patch['full_name'] = customer.fullName;
      else if (customer.firstName || customer.lastName) patch['full_name'] = joinName(customer.firstName, customer.lastName);
      if (customer.firstName) patch['first_name'] = customer.firstName;
      if (customer.lastName) patch['last_name'] = customer.lastName;
      if (customer.email) patch['email'] = customer.email;
      if (customer.city) { patch['city'] = customer.city; patch['primary_city'] = customer.city; }
      if (customer.address) patch['address'] = customer.address;
      if (customer.language) patch['preferred_language'] = customer.language;
      const { error } = await anyFrom(this.supabase)('online_customers').update(patch).eq('id', existing.id);
      if (error) throw error;
      onlineCustomerId = existing.id;
    } else {
      const { data, error } = await anyFrom(this.supabase)('online_customers')
        .insert({
          phone,
          tenant_id: tenantId,
          email: customer.email ?? null,
          first_name: customer.firstName ?? null,
          last_name: customer.lastName ?? null,
          full_name: customer.fullName ?? joinName(customer.firstName, customer.lastName),
          city: customer.city ?? null,
          primary_city: customer.city ?? null,
          address: customer.address ?? null,
          preferred_language: customer.language ?? 'fr',
          total_orders: 1,
          total_spent: totalTtc,
          first_order_at: new Date().toISOString(),
          last_order_at: new Date().toISOString(),
          first_utm_source: attribution?.utmSource ?? null,
          first_utm_campaign: attribution?.utmCampaign ?? null,
        })
        .select().single();
      if (error) throw error;
      onlineCustomerId = (data as Record<string, unknown>)['id'] as string;
    }

    // 4. Insert sales_documents (order). Once the DB migration is applied,
    // the trigger overwrites `number` atomically. Until then, this fallback
    // removes the broken nextval_safe call and avoids reusing the first number.
    let orderRow: Record<string, unknown> | null = null;
    let orderErr: unknown = null;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const orderNumber = await this.buildNextOrderNumber(tenantId, attempt);
      const { data, error } = await anyFrom(this.supabase)('sales_documents')
        .insert({
          doc_type: 'order',
          tenant_id: tenantId,
          number: orderNumber,
          customer_source: 'online',
          client_id: null,
          online_customer_id: onlineCustomerId,
          channel: 'storefront',
          storefront_order_status: 'pending',
          doc_date: new Date().toISOString().slice(0, 10),
          total_ht: totalHt,
          total_vat: totalVat,
          total_ttc: totalTtc,
          total_discount: 0,
          withholding_amount: 0,
          delivery_fee: deliveryFee,
          net_to_pay: totalTtc,
          notes: notes ?? null,
        })
        .select().single();

      if (!error) {
        orderRow = data as Record<string, unknown>;
        orderErr = null;
        break;
      }
      orderErr = error;
      if (!isUniqueViolation(error)) break;
    }
    if (orderErr || !orderRow) throw orderErr ?? new Error('order_create_failed');
    const orderId = (orderRow as Record<string, unknown>)['id'] as string;
    const orderNumber = (orderRow as Record<string, unknown>)['number'] as string | undefined;
    if (!orderNumber) throw new Error('order_number_failed');

    // 6. Insert lines
    const lineRows = lines.map((l, i) => ({
      document_id: orderId,
      tenant_id: tenantId,
      line_number: i + 1,
      article_id: l.articleId,
      description: l.description,
      quantity: l.quantity,
      unit_price_ht: l.unitPriceHt,
      discount_pct: 0,
      vat_rate: l.vatRate,
      total_ht: l.totalHt,
      total_ttc: l.totalTtc,
    }));
    const { error: linesErr } = await anyFrom(this.supabase)('sales_document_lines').insert(lineRows);
    if (linesErr) throw linesErr;

    // 7. Insert order attribution (always insert, even if empty — for funnel tracking)
    if (attribution) {
      await anyFrom(this.supabase)('order_attribution').insert({
        sales_document_id: orderId,
        tenant_id: tenantId,
        utm_source: attribution.utmSource ?? null,
        utm_medium: attribution.utmMedium ?? null,
        utm_campaign: attribution.utmCampaign ?? null,
        utm_content: attribution.utmContent ?? null,
        utm_term: attribution.utmTerm ?? null,
        landing_page: attribution.landingPage ?? null,
        referrer: attribution.referrer ?? null,
        ad_platform: attribution.adPlatform ?? null,
        ad_id: attribution.adId ?? null,
        adset_id: attribution.adsetId ?? null,
        campaign_id: attribution.campaignId ?? null,
        ad_name: attribution.adName ?? null,
        adset_name: attribution.adsetName ?? null,
        campaign_name: attribution.campaignName ?? null,
      });
    }

    // 8. Notify admin + recover any matching abandoned cart
    const displayName = customer.fullName ?? joinName(customer.firstName, customer.lastName) ?? phone;
    try {
      await anyFrom(this.supabase)('admin_notifications').insert({
        kind: 'order_new',
        tenant_id: tenantId,
        title: `Nouvelle commande ${orderNumber}`,
        message: `${displayName} — ${round3(totalTtc).toFixed(3)} TND · ${lines.length} article(s)`,
        link_path: `/storefront/orders/${orderId}`,
        entity_type: 'sales_document',
        entity_id: orderId,
        metadata: { orderNumber, totalTtc, customerPhone: phone, customerName: displayName, itemCount: lines.length },
      });
    } catch { /* best-effort; don't fail the order */ }

    try {
      await anyFrom(this.supabase)('abandoned_carts')
        .update({ status: 'recovered', recovered_sales_document_id: orderId })
        .eq('tenant_id', tenantId)
        .eq('phone', phone)
        .eq('status', 'active');
    } catch { /* best-effort */ }

    // 9. Optional thank-you page content snapshot
    const { data: thankYou } = await anyFrom(this.supabase)('store_pages')
      .select('title_fr, title_ar, title_en, content_fr, content_ar, content_en')
      .eq('tenant_id', tenantId)
      .eq('page_key', 'thank_you')
      .maybeSingle();

    return {
      orderId,
      orderNumber,
      onlineCustomerId,
      totalTtc,
      thankYouContent: thankYou
        ? {
            titleFr: (thankYou as Record<string, string | null>)['title_fr'] ?? null,
            titleAr: (thankYou as Record<string, string | null>)['title_ar'] ?? null,
            titleEn: (thankYou as Record<string, string | null>)['title_en'] ?? null,
            contentFr: (thankYou as Record<string, string | null>)['content_fr'] ?? null,
            contentAr: (thankYou as Record<string, string | null>)['content_ar'] ?? null,
            contentEn: (thankYou as Record<string, string | null>)['content_en'] ?? null,
          }
        : undefined,
    };
  }

  /** Reads the configured delivery fee from store_settings. Returns 0 if the
   *  table is empty or the column is null — never throws, since checkout must
   *  not fail just because the fee is missing. */
  private async fetchStoreDeliveryFee(tenantId: string): Promise<number> {
    try {
      const { data, error } = await anyFrom(this.supabase)('store_settings')
        .select('delivery_fee').eq('tenant_id', tenantId).limit(1).maybeSingle();
      if (error) return 0;
      const fee = Number((data as { delivery_fee?: number | null } | null)?.delivery_fee ?? 0);
      return Number.isFinite(fee) && fee > 0 ? round3(fee) : 0;
    } catch {
      return 0;
    }
  }

  private async buildNextOrderNumber(tenantId: string, offset: number): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `CMD-${year}-`;
    const { data } = await anyFrom(this.supabase)('sales_documents')
      .select('number')
      .eq('tenant_id', tenantId)
      .eq('doc_type', 'order')
      .like('number', `${prefix}%`)
      .order('number', { ascending: false })
      .limit(1);
    const last = Array.isArray(data) ? (data[0]?.number as string | undefined) : undefined;
    const parsed = last?.match(/^CMD-\d{4}-(\d+)$/);
    const next = (parsed ? Number(parsed[1]) : 0) + 1 + offset;
    return `${prefix}${String(next).padStart(4, '0')}`;
  }

  private async submitViaRpc(input: CheckoutSubmitInput): Promise<CheckoutSubmitResult | null> {
    const client = this.supabase as unknown as {
      rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
    };
    const { data, error } = await client.rpc('submit_storefront_checkout', {
      p_customer: input.customer,
      p_items: input.items,
      p_attribution: input.attribution ?? {},
      p_notes: input.notes ?? null,
    });
    if (error) {
      if (isMissingRpc(error)) return null;
      throw normalizeSupabaseError(error);
    }
    return mapCheckoutSubmitResult(data);
  }
}

// ============================================================
// STOREFRONT CUSTOMER ACCOUNT
// ============================================================

export class SupabaseStorefrontCustomerAccountRepository implements IStorefrontCustomerAccountRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getProfileByEmail(email: string, tenantId?: string): Promise<OnlineCustomer | null> {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return null;

    let query = anyFrom(this.supabase)('online_customers')
      .select('*')
      .ilike('email', normalizedEmail)
      .order('last_order_at', { ascending: false, nullsFirst: false });
    if (tenantId) query = query.eq('tenant_id', tenantId);

    const { data, error } = await query
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapOnlineCustomer(data) : null;
  }

  async saveProfile(input: SaveStorefrontAccountProfileInput): Promise<OnlineCustomer> {
    const email = normalizeEmail(input.email);
    if (!email) throw new Error('email_required');

    const existing = await this.getProfileByEmail(email, input.tenantId);
    if (existing) {
      const { data, error } = await anyFrom(this.supabase)('online_customers')
        .update({
          email,
          full_name: input.fullName ?? existing.fullName,
          phone: input.phone ? normalizePhone(input.phone) : existing.phone,
          city: input.city ?? existing.city,
          primary_city: input.city ?? existing.primaryCity,
          address: input.address ?? existing.address,
        })
        .eq('id', existing.id)
        .select()
        .single();
      if (error) throw error;
      return mapOnlineCustomer(data);
    }

    if (!input.phone) throw new Error('phone_required');
    const { data, error } = await anyFrom(this.supabase)('online_customers')
        .insert({
        tenant_id: input.tenantId,
        phone: normalizePhone(input.phone),
        email,
        full_name: input.fullName ?? null,
        city: input.city ?? null,
        primary_city: input.city ?? null,
        address: input.address ?? null,
        preferred_language: 'fr',
      })
      .select()
      .single();
    if (error) throw error;
    return mapOnlineCustomer(data);
  }

  async listOrdersByEmail(email: string, tenantId?: string): Promise<StorefrontAccountOrder[]> {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return [];

    let customerQuery = anyFrom(this.supabase)('online_customers')
      .select('id')
      .ilike('email', normalizedEmail);
    if (tenantId) customerQuery = customerQuery.eq('tenant_id', tenantId);

    const { data: customers, error: customersError } = await customerQuery;
    if (customersError) throw customersError;

    const customerIds = ((customers ?? []) as Array<{ id: string }>).map((customer) => customer.id);
    if (customerIds.length === 0) return [];

    let orderQuery = anyFrom(this.supabase)('sales_documents')
      .select('id, number, doc_date, storefront_order_status, total_ttc, delivery_fee, sales_document_lines(id, description, quantity, total_ttc, articles(image_url))')
      .eq('channel', 'storefront')
      .eq('doc_type', 'order')
      .in('online_customer_id', customerIds);
    if (tenantId) orderQuery = orderQuery.eq('tenant_id', tenantId);

    const { data, error } = await orderQuery.order('created_at', { ascending: false });
    if (error) throw error;

    return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
      id: row['id'] as string,
      orderNumber: row['number'] as string,
      docDate: row['doc_date'] as string,
      status: (row['storefront_order_status'] as StorefrontAccountOrder['status']) ?? null,
      totalTtc: Number(row['total_ttc'] ?? 0),
      deliveryFee: Number(row['delivery_fee'] ?? 0),
      lines: (((row['sales_document_lines'] as unknown[]) ?? []) as Array<Record<string, unknown>>)
        .map((line): StorefrontAccountOrderLine => ({
          id: line['id'] as string,
          description: (line['description'] as string) ?? '',
          imageUrl: readOrderLineArticleImage(line),
          quantity: Number(line['quantity'] ?? 0),
          totalTtc: Number(line['total_ttc'] ?? 0),
        })),
    }));
  }
}

function readOrderLineArticleImage(line: Record<string, unknown>): string | null {
  const article = line['articles'];
  if (!article || typeof article !== 'object') return null;
  const imageUrl = (article as Record<string, unknown>)['image_url'];
  return typeof imageUrl === 'string' && imageUrl.trim() ? imageUrl : null;
}

function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

function isUniqueViolation(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && 'code' in error && error.code === '23505');
}

function isMissingRpc(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as Record<string, unknown>;
  const code = String(record['code'] ?? '');
  const message = String(record['message'] ?? record['details'] ?? '');
  return code === 'PGRST202' || code === '42883' || /function .* does not exist|could not find the function/i.test(message);
}

function normalizeSupabaseError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record['message'] ?? record['details'] ?? record['hint'];
    if (typeof message === 'string' && message.trim()) return new Error(message);
  }
  return new Error(String(error));
}

function mapCheckoutSubmitResult(data: unknown): CheckoutSubmitResult {
  const row = (data ?? {}) as Record<string, unknown>;
  const thankYou = row['thankYouContent'] ?? row['thank_you_content'];
  return {
    orderId: String(row['orderId'] ?? row['order_id'] ?? ''),
    orderNumber: String(row['orderNumber'] ?? row['order_number'] ?? ''),
    onlineCustomerId: String(row['onlineCustomerId'] ?? row['online_customer_id'] ?? ''),
    totalTtc: Number(row['totalTtc'] ?? row['total_ttc'] ?? 0),
    thankYouContent: thankYou && typeof thankYou === 'object'
      ? {
          titleFr: ((thankYou as Record<string, unknown>)['titleFr'] ?? null) as string | null,
          titleAr: ((thankYou as Record<string, unknown>)['titleAr'] ?? null) as string | null,
          titleEn: ((thankYou as Record<string, unknown>)['titleEn'] ?? null) as string | null,
          contentFr: ((thankYou as Record<string, unknown>)['contentFr'] ?? null) as string | null,
          contentAr: ((thankYou as Record<string, unknown>)['contentAr'] ?? null) as string | null,
          contentEn: ((thankYou as Record<string, unknown>)['contentEn'] ?? null) as string | null,
        }
      : undefined,
  };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

// ============================================================
// STOREFRONT ANALYTICS
// ============================================================

export class SupabaseStorefrontAnalyticsRepository implements IStorefrontAnalyticsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getAnalytics(range: StorefrontAnalyticsRange): Promise<StorefrontAnalytics> {
    const { from, to } = range;

    // All storefront orders in range
    const { data: rawOrders } = await anyFrom(this.supabase)('sales_documents')
      .select('id, total_ttc, doc_date, created_at, online_customer_id, online_customers(city)')
      .eq('channel', 'storefront')
      .eq('doc_type', 'order')
      .gte('doc_date', from)
      .lte('doc_date', to);
    const orders = (rawOrders ?? []) as any[];

    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((s, o) => s + Number(o.total_ttc || 0), 0);
    const orderIds = orders.map((o) => o.id as string);

    let totalVisits = 0;
    const { data: rawVisits, error: visitsError } = await anyFrom(this.supabase)('storefront_visits')
      .select('visitor_id, created_at')
      .gte('created_at', `${from}T00:00:00`)
      .lte('created_at', `${to}T23:59:59`);
    if (!visitsError) {
      totalVisits = ((rawVisits ?? []) as unknown[]).length;
    }

    // Attribution rollups
    let revenueByCampaign: AttributionRollup[] = [];
    let revenueByAdset: AttributionRollup[] = [];
    let revenueByAd: AttributionRollup[] = [];
    if (orderIds.length) {
      const { data: attrs } = await anyFrom(this.supabase)('order_attribution')
        .select('sales_document_id, utm_campaign, campaign_name, adset_name, ad_name, campaign_id, adset_id, ad_id')
        .in('sales_document_id', orderIds);
      const attrList = (attrs ?? []) as any[];
      const orderRevenue = new Map<string, number>();
      for (const o of orders) orderRevenue.set(o.id, Number(o.total_ttc || 0));

      revenueByCampaign = rollupAttr(attrList, orderRevenue, (a) => a.campaign_name ?? a.utm_campaign ?? '—');
      revenueByAdset = rollupAttr(attrList, orderRevenue, (a) => a.adset_name ?? a.adset_id ?? '—');
      revenueByAd = rollupAttr(attrList, orderRevenue, (a) => a.ad_name ?? a.ad_id ?? '—');
    }

    // Top products
    let topProducts: TopProductRollup[] = [];
    if (orderIds.length) {
      const { data: rawLines } = await anyFrom(this.supabase)('sales_document_lines')
        .select('document_id, article_id, description, quantity, total_ttc')
        .in('document_id', orderIds);
      const byArticle = new Map<string, { name: string; units: number; revenue: number }>();
      for (const l of (rawLines ?? []) as any[]) {
        const aid = l.article_id as string;
        const name = l.description as string;
        const current = byArticle.get(aid) ?? { name, units: 0, revenue: 0 };
        current.units += Number(l.quantity || 0);
        current.revenue += Number(l.total_ttc || 0);
        byArticle.set(aid, current);
      }
      topProducts = Array.from(byArticle.entries())
        .map(([productId, v]) => ({
          productId,
          productName: v.name,
          unitsSold: v.units,
          revenue: v.revenue,
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
    }

    // Top categories (rough: via articles.category_id)
    let topCategories: { categoryId: string; name: string; revenue: number }[] = [];
    if (topProducts.length) {
      const articleIds = topProducts.map((p) => p.productId);
      const { data: articles } = await anyFrom(this.supabase)('articles')
        .select('id, category_id, article_categories(name)')
        .in('id', articleIds);
      const catRevenue = new Map<string, { name: string; revenue: number }>();
      for (const p of topProducts) {
        const a = (articles ?? []).find((x: any) => x.id === p.productId);
        if (!a || !a.category_id) continue;
        const cur = catRevenue.get(a.category_id) ?? {
          name: a.article_categories?.name ?? '—',
          revenue: 0,
        };
        cur.revenue += p.revenue;
        catRevenue.set(a.category_id, cur);
      }
      topCategories = Array.from(catRevenue.entries())
        .map(([categoryId, v]) => ({ categoryId, name: v.name, revenue: v.revenue }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
    }

    // Revenue by city
    const byCity = new Map<string, { orders: number; revenue: number }>();
    for (const o of orders) {
      const city = o.online_customers?.city ?? '—';
      const cur = byCity.get(city) ?? { orders: 0, revenue: 0 };
      cur.orders += 1;
      cur.revenue += Number(o.total_ttc || 0);
      byCity.set(city, cur);
    }
    const revenueByCity: CityRollup[] = Array.from(byCity.entries())
      .map(([city, v]) => ({ city, orders: v.orders, revenue: v.revenue }))
      .sort((a, b) => b.revenue - a.revenue);

    // New customers in range
    const { count: newCustomers = 0 } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('online_customers')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', from)
      .lte('created_at', `${to}T23:59:59`);

    // Repeat rate
    const { count: repeatCount = 0 } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('online_customers')
      .select('id', { count: 'exact', head: true })
      .gte('total_orders', 2);
    const { count: totalCust = 0 } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('online_customers')
      .select('id', { count: 'exact', head: true });
    const repeatCustomerRate = totalCust ? (repeatCount ?? 0) / (totalCust ?? 1) : 0;

    return {
      range,
      funnel: {
        visits: totalVisits,
        productViews: 0,
        checkoutStarted: 0,
        confirmed: totalOrders,
        delivered: 0,
      },
      revenueByCampaign,
      revenueByAdset,
      revenueByAd,
      topProducts,
      topCategories,
      revenueByCity,
      newCustomers: newCustomers ?? 0,
      repeatCustomerRate,
      abandonedCarts: 0,
      totalOrders,
      totalRevenue,
    };
  }
}

function rollupAttr(
  attrs: Array<Record<string, unknown>>,
  revenue: Map<string, number>,
  keyFn: (a: any) => string,
): AttributionRollup[] {
  const out = new Map<string, { orders: number; revenue: number }>();
  for (const a of attrs) {
    const orderId = a['sales_document_id'] as string;
    const rev = revenue.get(orderId) ?? 0;
    const key = keyFn(a) || '—';
    const cur = out.get(key) ?? { orders: 0, revenue: 0 };
    cur.orders += 1;
    cur.revenue += rev;
    out.set(key, cur);
  }
  return Array.from(out.entries())
    .map(([key, v]) => ({ key, label: key, orders: v.orders, revenue: v.revenue }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
}
